import { createContext, useCallback, useContext, useMemo, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, session } from "./api";

const AuthContext = createContext(/** @type {any} */ (null));

function useSession() {
  return useSyncExternalStore(
    session.subscribe,
    () => `${session.tokens?.access_token ?? ""}|${session.businessId ?? ""}`,
  );
}

export function AuthProvider({ children }) {
  const key = useSession();
  const qc = useQueryClient();
  const loggedIn = Boolean(session.tokens);

  const me = useQuery({
    queryKey: ["me", key.split("|")[0].slice(-12)],
    queryFn: () => api.get("/auth/me"),
    enabled: loggedIn,
    staleTime: 5 * 60_000,
  });

  const businesses = useMemo(() => me.data?.businesses ?? [], [me.data]);
  const active = businesses.find((b) => b.business.id === session.businessId) ?? businesses[0] ?? null;

  const login = useCallback(
    async (email, password) => {
      session.setTokens(await api.post("/auth/login", { email, password }));
      qc.clear();
    },
    [qc],
  );
  const register = useCallback(
    async (data) => {
      session.setTokens(await api.post("/auth/register", data));
      session.setBusiness(null);
      qc.clear();
    },
    [qc],
  );
  const logout = useCallback(() => {
    session.clear();
    qc.clear();
  }, [qc]);
  const switchBusiness = useCallback(
    (id) => {
      session.setBusiness(id);
      qc.invalidateQueries();
    },
    [qc],
  );

  const value = useMemo(() => {
    const perms = new Set(active?.permissions ?? []);
    /** @param {string} p */
    const can = (p) => {
      if (perms.has("*") || perms.has(p)) return true;
      if (["banking:approve", "payments:configure", "members:write", "settings:write"].includes(p))
        return false;
      return perms.has(`${p.split(":")[0]}:*`);
    };
    return {
      loggedIn,
      loading: loggedIn && me.isLoading,
      user: me.data?.user ?? null,
      businesses,
      business: active?.business ?? null,
      role: active?.role ?? null,
      can,
      login,
      register,
      logout,
      switchBusiness,
      refetch: me.refetch,
    };
  }, [
    loggedIn,
    me.isLoading,
    me.data,
    me.refetch,
    businesses,
    active,
    login,
    register,
    logout,
    switchBusiness,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** @returns {{loggedIn:boolean, loading:boolean, user:any, businesses:any[], business:any, role:string|null, can:(p:string)=>boolean, login:Function, register:Function, logout:Function, switchBusiness:Function, refetch:Function}} */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth faqat AuthProvider ichida");
  return ctx;
}

export const ROLE_LABELS = {
  owner: "Egasi",
  manager: "Menejer",
  accountant: "Buxgalter",
  cashier: "Kassir",
  employee: "Xodim",
};
